import { useEffect, useMemo, useState } from "react";
import { Table, Spinner, Button, Input } from "reactstrap";
import { saveAs } from "file-saver";
import FeatherIcon from "feather-icons-react";
import { getPadInfoReport, exportPadInfoReportExcel } from "../TenantForm/Helpers/PadInfo/api";
import { categoryMeta, money, nextMonthYm, formatMonthLabel, PAD_STATE_META, SOURCE_META } from "../TenantForm/Helpers/PadInfo/padInfoTypes";

/**
 * "PAD" tab of the Master-Sheet Report modal. Previews the same two sheets accounting gets
 * in the Excel download:
 *   Sheet 1 — PAD updates (only units with PAD data), grouped by category, for a month
 *   Sheet 2 — every unit, colour-coded: green data / yellow document only / red nothing / grey vacant
 */
const PadInfoTab = ({ masterSheetData }) => {
  const mastersheetId = masterSheetData?._id;
  const [month, setMonth] = useState(() => nextMonthYm());
  const [mode, setMode] = useState("month"); // "month" | "current"
  const [view, setView] = useState("updates"); // "updates" | "units"
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [downloading, setDownloading] = useState(false);

  const effectiveMonth = mode === "month" ? month : null;

  useEffect(() => {
    let active = true;
    if (!mastersheetId) return undefined;
    setLoading(true);
    setError(null);
    getPadInfoReport(mastersheetId, effectiveMonth)
      .then((resp) => {
        if (!active) return;
        if (!resp?.success) throw new Error(resp?.message || "Failed to load report");
        setReport(resp.data);
      })
      .catch((err) => active && setError(err?.response?.data?.message || err.message || "Failed to load PAD report"))
      .finally(() => active && setLoading(false));
    return () => { active = false; };
  }, [mastersheetId, effectiveMonth]);

  const handleExport = async () => {
    setDownloading(true);
    try {
      const blob = await exportPadInfoReportExcel(mastersheetId, effectiveMonth);
      const name = `PAD-Info_${(masterSheetData?.building_name || "sheet").replace(/[^a-z0-9_-]+/gi, "_")}_${effectiveMonth || "current"}.xlsx`;
      saveAs(new Blob([blob]), name);
    } catch (err) {
      console.error("PAD info excel export failed:", err);
    } finally {
      setDownloading(false);
    }
  };

  const t = report?.totals;
  const updateCount = useMemo(() => (report?.updates || []).reduce((s, g) => s + g.rows.length, 0), [report]);

  const rowClass = (u) => {
    if (u.occupancy === "VACANT") return "table-secondary";
    if (u.source === "DATA") return "table-success";
    if (u.doc_na) return "table-info";
    if (u.source === "DOCUMENT") return "table-warning";
    return "table-danger";
  };

  return (
    <div>
      <style>{`
        .pit-toolbar { display: flex; flex-wrap: wrap; gap: 10px; align-items: center; margin-bottom: 12px; }
        .pit-seg { display: inline-flex; border: 1px solid #D6DEEA; border-radius: 8px; overflow: hidden; }
        .pit-seg button { border: none; background: #fff; padding: 6px 12px; font-size: 12.5px; font-weight: 600; color: #33415C; cursor: pointer; font-family: inherit; }
        .pit-seg button.on { background: #0C447C; color: #fff; }
        .pit-seg button + button { border-left: 1px solid #D6DEEA; }
        .pit-cat { display: flex; align-items: center; gap: 8px; padding: 6px 10px; border-radius: 8px 8px 0 0; color: #fff; font-weight: 700; font-size: 13px; margin-top: 14px; }
        .pit-cat span.n { background: rgba(255,255,255,0.25); padding: 1px 8px; border-radius: 999px; font-size: 12px; }
        .pit-details { max-width: 380px; line-height: 1.35; }
        .pit-notes { max-width: 260px; white-space: pre-wrap; color: #3D4C63; }
        .pit-legend { display: flex; flex-wrap: wrap; gap: 8px; margin: 6px 0 10px; font-size: 12px; }
        .pit-legend span { padding: 3px 10px; border-radius: 6px; border: 1px solid rgba(0,0,0,0.06); }
        .pit-verified { color: #15803D; font-weight: 600; white-space: nowrap; }
        .pit-unverified { color: #B45309; font-weight: 600; white-space: nowrap; }
        .pit-table td, .pit-table th { vertical-align: top; }
        .pit-details { min-width: 240px; }
        .pit-notes { min-width: 140px; }
        .pit-section { break-inside: avoid; }
        .pit-empty { border: 1px solid #E4E9F2; border-top: none; border-radius: 0 0 8px 8px; background: #FBFCFE;
          padding: 10px 14px; font-size: 12.5px; font-style: italic; color: #94A3B8; }
        .pit-money { font-variant-numeric: tabular-nums; font-weight: 600; white-space: nowrap; }
        .pit-files { display: flex; flex-direction: column; gap: 3px; }
        .pit-files a { font-size: 12px; color: #1D4ED8; display: inline-flex; align-items: center; gap: 4px;
          max-width: 160px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
        .pit-title { font-size: 14px; font-weight: 700; color: #0F1F35; margin: 0 0 4px; }
        .pit-gen { font-weight: 500; color: #64748B; }
        .pit-counts { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 10px; margin-top: 18px; }
        .pit-count { border-radius: 8px; margin-top: 0; justify-content: space-between; }
        @media (max-width: 560px) { .pit-counts { grid-template-columns: 1fr; } }

        /* Print: drop the chrome, keep the data, never split a category across pages. */
        @media print {
          @page { size: landscape; margin: 10mm; }
          .pit-toolbar, .pit-legend { display: none !important; }
          .pit-section { break-inside: avoid; page-break-inside: avoid; }
          .pit-cat { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
          .pit-table { font-size: 10.5px; }
          .pit-details, .pit-notes { max-width: none; }
          .pit-files a { color: #0F1F35; text-decoration: underline; }
          a[href]:after { content: ""; }
        }
      `}</style>

      <div className="pit-toolbar">
        <div className="pit-seg" role="tablist" aria-label="Report scope">
          <button type="button" className={mode === "month" ? "on" : ""} onClick={() => setMode("month")}>Month</button>
          <button type="button" className={mode === "current" ? "on" : ""} onClick={() => setMode("current")}>Current status</button>
        </div>
        {mode === "month" && (
          <Input type="month" bsSize="sm" style={{ width: 170 }} value={month} onChange={(e) => e.target.value && setMonth(e.target.value)} />
        )}
        <div className="pit-seg ms-2" role="tablist" aria-label="Sheet">
          <button type="button" className={view === "updates" ? "on" : ""} onClick={() => setView("updates")}>Sheet 1 · PAD updates</button>
          <button type="button" className={view === "units" ? "on" : ""} onClick={() => setView("units")}>Sheet 2 · All units</button>
        </div>
        <Button size="sm" color="light" className="ms-auto" onClick={() => window.print()} disabled={!report}>
          <FeatherIcon icon="printer" size={14} /> Print
        </Button>
        <Button size="sm" color="success" onClick={handleExport} disabled={downloading || !mastersheetId}>
          {downloading ? <Spinner size="sm" /> : <><FeatherIcon icon="download" size={13} className="me-1" /> Download PAD Excel</>}
        </Button>
      </div>

      {loading ? (
        <div className="text-center p-4"><Spinner color="primary" /> Loading PAD report…</div>
      ) : error ? (
        <p className="text-danger p-3">{error}</p>
      ) : !report ? (
        <p className="text-muted p-3">No data available.</p>
      ) : (
        <>
          {/* The heading the workbook carries: scope, property, date. */}
          <div className="pit-title">
            PAD Changes · {mode === "month" ? formatMonthLabel(effectiveMonth) : "Current status"} · {[report.property_name, report.building_name].filter(Boolean).join(" · ")}
            <span className="pit-gen"> (Generated on {String(report.generatedAt || "").slice(0, 10)})</span>
          </div>

          {view === "updates" ? (
            <div>
              {updateCount === 0 && report.carryover.length === 0 && (report.resumes || []).length === 0 ? (
                <p className="text-muted p-3 mb-0">
                  {mode === "month" ? `No PAD changes recorded for ${formatMonthLabel(effectiveMonth)}.` : "No PAD information recorded for this property yet."}
                  {t.none + t.documentOnly > 0 ? ` ${t.none + t.documentOnly} occupied unit${t.none + t.documentOnly === 1 ? "" : "s"} still need PAD info — see Sheet 2.` : ""}
                </p>
              ) : null}
              {[
                ...report.updates,
                ...((report.resumes || []).length ? [{ category: "RESUME", label: "PAD resumes this month — restart the pull", rows: report.resumes }] : []),
                ...(report.carryover.length ? [{ category: "CARRYOVER", label: "Ongoing temporary stops — do not pull", rows: report.carryover }] : []),
              ].map((g) => {
                const meta =
                  g.category === "CARRYOVER" ? { color: "#546E7A", icon: "pause" }
                  : g.category === "RESUME" ? { color: "#0F766E", icon: "play" }
                  : categoryMeta(g.category);
                return (
                  <div key={g.category} className="pit-section">
                    <div className="pit-cat" style={{ background: meta.color }}>
                      <FeatherIcon icon={meta.icon} size={14} /> {g.label} <span className="n">{g.rows.length}</span>
                    </div>
                    {/* Every category renders, empty ones included: "nothing was delayed" and
                        "the Delay section is missing" must not look the same. */}
                    {g.rows.length === 0 ? (
                      <div className="pit-empty">{mode === "month" ? "No changes this month." : "Nothing recorded."}</div>
                    ) : (
                      <Table hover responsive size="sm" className="pit-table mb-0" style={{ border: "1px solid #E4E9F2", borderTop: "none" }}>
                        <thead className="table-light">
                          <tr>
                            <th style={{ width: 70 }}>Unit</th>
                            <th>Resident</th>
                            <th style={{ width: 110 }}>Effective</th>
                            <th className="text-end" style={{ width: 110 }}>Total Rent</th>
                            <th>Details</th>
                            <th>Notes</th>
                            <th style={{ width: 160 }}>Attachments</th>
                            <th style={{ width: 150 }}>Verified</th>
                          </tr>
                        </thead>
                        <tbody>
                          {g.rows.map((r) => (
                            <tr key={r.pad_id || `${r.unit_no}-${r.effective_date}`}>
                              <td><strong>{r.unit_no}</strong></td>
                              <td>{r.tenant || "—"}</td>
                              <td>{r.effective_date || "—"}</td>
                              <td className="text-end pit-money">{r.rent != null ? money(r.rent) : "—"}</td>
                              <td><div className="pit-details">{r.details}</div></td>
                              <td><div className="pit-notes">{r.notes || "—"}</div></td>
                              <td>
                                {(r.attachments || []).length === 0 ? (
                                  <span className="text-muted">—</span>
                                ) : (
                                  <div className="pit-files">
                                    {r.attachments.map((a, i) => (
                                      <a key={i} href={a.url} target="_blank" rel="noopener noreferrer" title={a.filename}>
                                        <FeatherIcon icon="paperclip" size={11} /> {a.filename}
                                      </a>
                                    ))}
                                  </div>
                                )}
                              </td>
                              <td>{r.verified ? <span className="pit-verified">Yes{r.verified_by ? ` — ${r.verified_by}` : ""}</span> : <span className="pit-unverified">Not yet</span>}</td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    )}
                  </div>
                );
              })}
              {/* The suite counts close Sheet 1 in the same bars the categories use; they moved
                  here from the top of the mastersheet. */}
              <div className="pit-counts">
                {[
                  ["Suites", t.unitsTotal, "#1F3864"],
                  ["Occupied", t.occupied, categoryMeta("NEW").color],
                  ["Vacant", t.vacant, categoryMeta("STOP").color],
                ].map(([label, n, color]) => (
                  <div key={label} className="pit-cat pit-count" style={{ background: color }}>
                    {label} <span className="n">{n}</span>
                  </div>
                ))}
              </div>
            </div>
          ) : (

            <div>
              <div className="pit-legend">
                <span style={{ background: SOURCE_META.DATA.bg }}>PAD data in Rentdigicare</span>
                <span style={{ background: SOURCE_META.DOCUMENT.bg }}>PAD document only — no data</span>
                <span style={{ background: SOURCE_META.NONE.bg }}>Nothing available</span>
                <span style={{ background: "#E7E6E6" }}>Vacant</span>
                <span style={{ background: "#DDEBF7" }}>Marked N/A (not on PAD)</span>
              </div>
              <Table hover responsive size="sm" className="pit-table">
                <thead className="table-light">
                  <tr>
                    <th>Unit</th><th>Resident</th><th>Occupancy</th><th>PAD status</th><th>Source</th><th>Category</th><th>Effective</th><th className="text-end">Total Rent</th><th>Pull day</th><th>Details</th><th>Notes</th><th>Verified</th><th>Doc updated</th>
                  </tr>
                </thead>
                <tbody>
                  {report.units.map((u) => {
                    const st = PAD_STATE_META[u.pad_state] || PAD_STATE_META.NO_PAD;
                    return (
                      <tr key={u.unit_no} className={rowClass(u)}>
                        <td><strong>{u.unit_no}</strong></td>
                        <td>{u.tenant || (u.occupancy === "VACANT" ? <em className="text-muted">vacant</em> : "—")}</td>
                        <td>{u.occupancy === "VACANT" ? (u.incoming_month ? `Vacant · prospect ${formatMonthLabel(u.incoming_month)}` : "Vacant") : "Occupied"}</td>
                        <td>{u.doc_na && u.source !== "DATA" ? "Not on PAD (N/A)" : st.label}</td>
                        <td>{u.occupancy === "VACANT" && u.source === "NONE" ? "—" : SOURCE_META[u.source]?.label}</td>
                        <td>{u.category_label || "—"}</td>
                        <td>{u.effective_date || "—"}</td>
                        <td className="text-end pit-money">{u.rent != null ? money(u.rent) : "—"}</td>
                        <td>{u.pull_day ?? "—"}</td>
                        <td><div className="pit-details">{u.details}</div></td>
                        <td><div className="pit-notes">{u.notes || "—"}</div></td>
                        <td>{u.source === "DATA" ? (u.verified ? <span className="pit-verified">Yes{u.verified_by ? ` — ${u.verified_by}` : ""}</span> : <span className="pit-unverified">Not yet</span>) : "—"}</td>
                        <td>{u.doc_last_updated || "—"}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </Table>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export { PadInfoTab };
